import { supabase } from '../lib/supabase';
import { notificationService } from './notificationService';

class AppointmentService {
  // Get available time slots for a specific date
  async getAvailableSlots(date, serviceType = 'service') {
    try {
      // Get day of week (0 = Sunday, 6 = Saturday)
      const dayOfWeek = new Date(date).getDay();
      
      // Get configured slots for this day
      const { data: slots, error: slotsError } = await supabase
        .from('appointment_slots')
        .select('*')
        .eq('day_of_week', dayOfWeek)
        .eq('is_active', true);
      
      if (slotsError) throw slotsError;
      if (!slots || slots.length === 0) return { success: true, data: [] };

      // Check if date is blocked
      const { data: blockedDates, error: blockedError } = await supabase
        .from('blocked_dates')
        .select('*')
        .eq('blocked_date', date);
      
      if (blockedError) throw blockedError;
      if (blockedDates && blockedDates.length > 0) {
        return { success: true, data: [], blocked: true, reason: blockedDates[0].reason };
      }

      // Get existing appointments for this date
      const { data: appointments, error: appointmentsError } = await supabase
        .from('appointments')
        .select('appointment_time, duration_minutes')
        .eq('appointment_date', date)
        .neq('status', 'cancelled');
      
      if (appointmentsError) throw appointmentsError;

      // Generate available time slots
      const availableSlots = [];
      const slot = slots[0]; // Assuming one slot config per day
      
      const startTime = new Date(`2000-01-01 ${slot.start_time}`);
      const endTime = new Date(`2000-01-01 ${slot.end_time}`);
      const slotDuration = 60; // 60 minutes per slot
      
      while (startTime < endTime) {
        const timeString = startTime.toTimeString().substring(0, 5);
        
        // Check if this time is already booked
        const isBooked = appointments?.some(apt => {
          const aptTime = apt.appointment_time.substring(0, 5);
          return aptTime === timeString;
        });
        
        if (!isBooked) {
          availableSlots.push({
            time: timeString,
            display: this.formatTime(timeString)
          });
        }
        
        startTime.setMinutes(startTime.getMinutes() + slotDuration);
      }

      return { success: true, data: availableSlots };
    } catch (error) {
      console.error('Error getting available slots:', error);
      return { success: false, error: error.message };
    }
  }

  // Book an appointment
  async bookAppointment(appointmentData) {
    try {
      const { error } = await supabase
        .from('appointments')
        .insert({
          customer_name: appointmentData.name,
          customer_email: appointmentData.email,
          customer_phone: appointmentData.phone,
          service_type: appointmentData.serviceType,
          appointment_date: appointmentData.date,
          appointment_time: appointmentData.time,
          vehicle_info: appointmentData.vehicleInfo,
          notes: appointmentData.notes,
          status: 'scheduled'
        });
      // No .select()/.single(): anon has INSERT-only RLS on appointments.

      if (error) throw error;

      // Send email notification to owner
      try {
        await notificationService.sendAppointmentNotification({
          name: appointmentData.name,
          email: appointmentData.email,
          phone: appointmentData.phone,
          type: appointmentData.serviceType,
          date: appointmentData.date,
          time: appointmentData.time,
          vehicle: appointmentData.vehicleInfo,
          notes: appointmentData.notes
        });
        console.log('✅ Appointment notification sent');
      } catch (emailError) {
        console.error('Failed to send appointment notification:', emailError);
        // Don't block appointment booking if email fails
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error booking appointment:', error);
      return { success: false, error: error.message };
    }
  }

  // Get appointments for admin view
  async getAppointments(filters = {}) {
    try {
      let query = supabase
        .from('appointments')
        .select('*')
        .order('appointment_date', { ascending: true })
        .order('appointment_time', { ascending: true });

      if (filters.date) {
        query = query.eq('appointment_date', filters.date);
      }
      
      if (filters.status) {
        query = query.eq('status', filters.status);
      }

      if (filters.serviceType) {
        query = query.eq('service_type', filters.serviceType);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching appointments:', error);
      return { success: false, error: error.message };
    }
  }

  // Update appointment status
  async updateAppointmentStatus(id, status) {
    try {
      const { data, error } = await supabase
        .from('appointments')
        .update({ 
          status,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      
      return { success: true, data };
    } catch (error) {
      console.error('Error updating appointment:', error);
      return { success: false, error: error.message };
    }
  }

  // Cancel appointment
  async cancelAppointment(id) {
    return this.updateAppointmentStatus(id, 'cancelled');
  }

  // Update business hours
  async updateBusinessHours(dayOfWeek, startTime, endTime, isActive = true) {
    try {
      const { data, error } = await supabase
        .from('appointment_slots')
        .upsert({
          day_of_week: dayOfWeek,
          start_time: startTime,
          end_time: endTime,
          is_active: isActive
        })
        .select()
        .single();

      if (error) throw error;
      
      return { success: true, data };
    } catch (error) {
      console.error('Error updating business hours:', error);
      return { success: false, error: error.message };
    }
  }

  // Block a date
  async blockDate(date, reason = '') {
    try {
      const { data, error } = await supabase
        .from('blocked_dates')
        .insert({
          blocked_date: date,
          reason
        })
        .select()
        .single();

      if (error) throw error;
      
      return { success: true, data };
    } catch (error) {
      console.error('Error blocking date:', error);
      return { success: false, error: error.message };
    }
  }

  // Unblock a date
  async unblockDate(date) {
    try {
      const { error } = await supabase
        .from('blocked_dates')
        .delete()
        .eq('blocked_date', date);

      if (error) throw error;
      
      return { success: true };
    } catch (error) {
      console.error('Error unblocking date:', error);
      return { success: false, error: error.message };
    }
  }

  // Format time for display
  formatTime(timeString) {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${period}`;
  }

  // Get service types
  getServiceTypes() {
    return [
      { value: 'service', label: 'Vehicle Service', icon: '🔧' },
      { value: 'test-drive', label: 'Test Drive', icon: '🚗' },
      { value: 'consultation', label: 'Sales Consultation', icon: '💼' },
      { value: 'inspection', label: 'Vehicle Inspection', icon: '🔍' },
      { value: 'trade-in', label: 'Trade-In Appraisal', icon: '🔄' }
    ];
  }
}

export default new AppointmentService();
